import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  Construction,
  Mail,
  Schedule,
  Storage,
  CloudUpload,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Versteckter Admin-Hub für Tools, die nicht im normalen Menü stehen sollen
 * (Datenbank-/Kataloge-Pflege, System-Mail-Templates, …). Aufruf:
 * /admin/dev — kein Eintrag im AppShell, nur per direkter URL.
 */

interface DevEntry {
  path: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const ENTRIES: DevEntry[] = [
  {
    path: '/admin/sysreg',
    title: 'System Registry',
    description:
      'Bearbeiten der system-internen Settings (Setting-Tabelle). Vorsicht: Änderungen können das System irreversibel beschädigen.',
    icon: <Storage fontSize='large' />,
  },
  {
    path: '/admin/fints-institutes',
    title: 'FinTS-Listen Management',
    description:
      'Bank-Katalog für HBCI/FinTS-Anbindung. Upload der ZKA-Excel-Liste (Bankleitzahlen + FinTS-URLs), optional als Migration für alle Kunden.',
    icon: <AccountBalance fontSize='large' />,
  },
  {
    path: '/admin/system_mail/list',
    title: 'System-Mail-Vorlagen',
    description:
      'Verwaltung der System-Mail-Templates (Benachrichtigungen, Reminder, automatisierte E-Mails).',
    icon: <Mail fontSize='large' />,
  },
  {
    path: '/admin/dev/processmanager',
    title: 'ProcessManager (inkl. Automatisierungen)',
    description:
      'Roh-Zugriff auf die ProcessManager-Tabelle: Trigger einzelner Einträge, Edit von data/State/Tries, Bulk-Export/Import, Sperrzeiten der PM-Typen. Für Support & Debug — Änderungen sind nicht rückgängig zu machen.',
    icon: <Schedule fontSize='large' />,
  },
  {
    path: '/admin/dev/seed',
    title: 'Stammdaten-Seed / Katalog-Import',
    description:
      'Katalog-JSONs (Amtsgerichte, Grundbuch-Gemarkungen je Bundesland) hochladen und daraus eine idempotente Seed-Migration erzeugen. Ersetzt das manuelle DB-/Skript-Seeding — die Migration wird in Dev geprüft und rollt bei allen Instanzen aus.',
    icon: <CloudUpload fontSize='large' />,
  },
];

export const DevHubOverview: React.FC = () => (
  <Box sx={{ p: 2 }}>
    <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
      <Construction />
      <Typography variant='h5'>Admin-Hub (intern)</Typography>
    </Stack>

    <Alert severity='warning' sx={{ mb: 3 }}>
      Versteckte Admin-Werkzeuge. Diese Seiten sind nicht im Standardmenü
      verlinkt, weil sie bei falscher Benutzung das System beschädigen können.
      Nur für Lextorbyte-Entwickler / Hersteller-Admin gedacht.
    </Alert>

    <Stack spacing={2}>
      {ENTRIES.map(entry => (
        <Card key={entry.path} variant='outlined'>
          <CardActionArea component={RouterLink} to={entry.path}>
            <CardContent>
              <Stack direction='row' spacing={2} alignItems='flex-start'>
                <Box sx={{ color: 'primary.main', mt: 0.5 }}>{entry.icon}</Box>
                <Box>
                  <Typography variant='subtitle1' fontWeight={600}>
                    {entry.title}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {entry.description}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.disabled'
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {entry.path}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Stack>
  </Box>
);
